/**
 * Hospital Configuration Service
 * Manages live hospital branding, banking details, currency, session policies,
 * email verification gating (OTP), and immutable audit logs.
 */

import { db, auth } from '../lib/firebase';
import {
  collection,
  doc,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import hospitalConfigDefault from '../config/hospitalConfig';

const CONFIG_DOC_PATH = 'hospital_config/default';
const SESSION_STORAGE_KEY = 'hospital_config_edit_session';
const SESSION_DURATION_MINUTES = 30;

/**
 * Deep merge default static config with live Firestore values
 */
export const mergeConfig = (defaultConfig, liveData) => {
  if (!liveData) return defaultConfig;

  return {
    ...defaultConfig,
    ...liveData,
    bank: {
      ...defaultConfig.bank,
      ...(liveData.bank || {})
    },
    currency: {
      ...defaultConfig.currency,
      ...(liveData.currency || {})
    },
    sessionSecurity: {
      ...defaultConfig.sessionSecurity,
      ...(liveData.sessionSecurity || {})
    },
    emailNotifications: {
      ...defaultConfig.emailNotifications,
      ...(liveData.emailNotifications || {}),
      types: {
        ...(defaultConfig.emailNotifications?.types || {}),
        ...(liveData.emailNotifications?.types || {})
      }
    }
  };
};

/**
 * Get current hospital configuration (Firestore with local fallback)
 */
export const getHospitalConfig = async () => {
  try {
    const configDoc = await getDoc(doc(db, 'hospital_config', 'default'));
    if (configDoc.exists()) {
      return mergeConfig(hospitalConfigDefault, configDoc.data());
    }
    return hospitalConfigDefault;
  } catch (error) {
    console.warn('⚠️ [HospitalConfig] Falling back to default static config:', error?.message);
    return hospitalConfigDefault;
  }
};

/**
 * Subscribe to live hospital configuration updates
 */
export const subscribeHospitalConfig = (callback, onError) => {
  try {
    return onSnapshot(
      doc(db, 'hospital_config', 'default'),
      (docSnap) => {
        if (docSnap.exists()) {
          callback(mergeConfig(hospitalConfigDefault, docSnap.data()));
        } else {
          callback(hospitalConfigDefault);
        }
      },
      (error) => {
        console.warn('⚠️ [HospitalConfig] Subscription error, using fallback:', error?.message);
        callback(hospitalConfigDefault);
        if (onError) onError(error);
      }
    );
  } catch (error) {
    console.warn('⚠️ [HospitalConfig] Could not attach listener:', error?.message);
    callback(hospitalConfigDefault);
    return () => {};
  }
};

/**
 * Request an Email Authentication Code (6-digit OTP) to unlock configuration editing
 * Dispatches code to Admin's registered email and persists verification request
 */
export const requestEmailAuthCode = async (adminUser) => {
  const currentUid = adminUser?.uid || auth?.currentUser?.uid;
  if (!currentUid) {
    throw new Error('User must be authenticated as Administrator');
  }

  const adminEmail = (adminUser?.email || auth?.currentUser?.email || '').trim().toLowerCase();
  if (!adminEmail) {
    throw new Error('Administrator does not have a registered email address');
  }

  // Generate a random 6-digit numeric OTP
  const otpCode = Math.floor(100000 + Math.random() * 900000).toString();
  const expiresAt = new Date(Date.now() + 10 * 60 * 1000); // 10 minutes

  try {
    const codeDocRef = await addDoc(collection(db, 'admin_auth_codes'), {
      admin_id: currentUid,
      admin_name: adminUser?.displayName || adminUser?.full_name || auth?.currentUser?.displayName || 'Administrator',
      admin_email: adminEmail,
      code: otpCode,
      purpose: 'hospital_config_edit',
      used: false,
      attempts: 0,
      created_at: serverTimestamp(),
      expires_at: Timestamp.fromDate(expiresAt)
    });

    console.log(`🔐 [HospitalConfig] Auth code generated for ${adminEmail} (Doc ID: ${codeDocRef.id})`);

    return {
      success: true,
      codeId: codeDocRef.id,
      email: adminEmail,
      expiresAt,
      // Dev helper for immediate offline verification if SMTP is delayed
      devCode: process.env.NODE_ENV === 'development' ? otpCode : undefined
    };
  } catch (error) {
    console.error('❌ [HospitalConfig] Failed to create auth code:', error);
    throw error;
  }
};

/**
 * Verify the entered 6-digit authentication code
 */
export const verifyEmailAuthCode = async (adminUser, enteredCode) => {
  const currentUid = adminUser?.uid || auth?.currentUser?.uid;
  if (!currentUid) throw new Error('User not authenticated');
  const cleanCode = (enteredCode || '').trim();
  if (cleanCode.length !== 6) {
    throw new Error('Please enter a valid 6-digit verification code');
  }

  try {
    const q = query(
      collection(db, 'admin_auth_codes'),
      where('admin_id', '==', currentUid),
      where('used', '==', false),
      limit(5)
    );

    const snap = await (await import('firebase/firestore')).getDocs(q);
    const now = Date.now();
    let matchingDoc = null;

    snap.forEach((d) => {
      const data = d.data();
      const expTime = data.expires_at?.toDate?.()?.getTime() || 0;
      if (data.code === cleanCode && expTime > now) {
        matchingDoc = d;
      }
    });

    if (!matchingDoc) {
      throw new Error('Invalid or expired verification code. Please check your email or request a new one.');
    }

    // Mark as used
    await updateDoc(doc(db, 'admin_auth_codes', matchingDoc.id), {
      used: true,
      used_at: serverTimestamp()
    });

    // Store active editing session token
    const sessionToken = {
      adminId: adminUser.uid,
      adminEmail: adminUser.email,
      verifiedAt: now,
      expiresAt: now + SESSION_DURATION_MINUTES * 60 * 1000
    };

    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionToken));

    return {
      success: true,
      expiresAt: sessionToken.expiresAt
    };
  } catch (error) {
    console.error('❌ [HospitalConfig] Verification failed:', error);
    throw error;
  }
};

/**
 * Check if the active browser session has a valid, unexpired edit session
 */
export const isEditSessionActive = (currentAdminId) => {
  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!stored) return false;
    const session = JSON.parse(stored);
    if (!session || !session.expiresAt) return false;
    if (Date.now() > session.expiresAt) {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      return false;
    }
    if (currentAdminId && session.adminId !== currentAdminId) {
      return false;
    }
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Get remaining session seconds
 */
export const getEditSessionRemainingSeconds = () => {
  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!stored) return 0;
    const session = JSON.parse(stored);
    const remaining = Math.max(0, Math.floor((session.expiresAt - Date.now()) / 1000));
    return remaining;
  } catch (e) {
    return 0;
  }
};

/**
 * Lock edit session manually
 */
export const clearEditSession = () => {
  sessionStorage.removeItem(SESSION_STORAGE_KEY);
};

/**
 * Save updates for a specific module of Hospital Configuration
 * Automatically writes an immutable entry into config_audit_logs
 */
export const updateHospitalConfigModule = async (moduleKey, moduleData, adminUser) => {
  if (!isEditSessionActive(adminUser?.uid)) {
    throw new Error('Editing session has expired or is locked. Please re-authenticate via email code.');
  }

  try {
    // 1. Fetch current data to compute diff
    const currentConfig = await getHospitalConfig();
    const previousValues = currentConfig[moduleKey] || null;

    // 2. Prepare payload
    let updatePayload = {};
    if (moduleKey === 'identity') {
      updatePayload = {
        name: moduleData.name,
        fullName: moduleData.fullName,
        tagline: moduleData.tagline,
        logo: moduleData.logo
      };
    } else if (moduleKey === 'contact') {
      updatePayload = {
        address: moduleData.address,
        phone: moduleData.phone,
        email: moduleData.email
      };
    } else if (moduleKey === 'bank') {
      updatePayload = {
        bank: {
          accountName: moduleData.accountName,
          accountNumber: moduleData.accountNumber,
          bankName: moduleData.bankName,
          branch: moduleData.branch
        }
      };
    } else if (moduleKey === 'currency') {
      updatePayload = {
        currency: {
          code: moduleData.code,
          symbol: moduleData.symbol,
          locale: moduleData.locale
        }
      };
    } else if (moduleKey === 'sessionSecurity') {
      updatePayload = {
        sessionSecurity: {
          idleTimeoutMinutes: Number(moduleData.idleTimeoutMinutes) || 60,
          warningDurationSeconds: Number(moduleData.warningDurationSeconds) || 60,
          lockTimeoutMinutes: Number(moduleData.lockTimeoutMinutes) || 60,
          autoSaveDraftsOnLock: Boolean(moduleData.autoSaveDraftsOnLock),
          continuousActivityGraceMinutes: Number(moduleData.continuousActivityGraceMinutes) || 5
        }
      };
    } else if (moduleKey === 'emailNotifications') {
      updatePayload = {
        emailNotifications: {
          enabled: Boolean(moduleData.enabled),
          fromName: moduleData.fromName,
          supportEmail: moduleData.supportEmail,
          patientPortalUrl: moduleData.patientPortalUrl,
          types: moduleData.types || {}
        }
      };
    }

    // 3. Save to Firestore
    await setDoc(
      doc(db, 'hospital_config', 'default'),
      {
        ...updatePayload,
        updated_at: serverTimestamp(),
        updated_by: adminUser.uid,
        updated_by_name: adminUser.displayName || adminUser.full_name || 'Administrator',
        updated_by_email: adminUser.email || ''
      },
      { merge: true }
    );

    // 4. Record Immutable Audit Log
    await addDoc(collection(db, 'config_audit_logs'), {
      admin_id: adminUser.uid,
      admin_name: adminUser.displayName || adminUser.full_name || 'Administrator',
      admin_email: adminUser.email || '',
      module: moduleKey,
      previous_values: previousValues,
      new_values: moduleData,
      timestamp: serverTimestamp(),
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown'
    });

    console.log(`✅ [HospitalConfig] Module '${moduleKey}' successfully updated and logged in audit trail.`);
    return { success: true };
  } catch (error) {
    console.error(`❌ [HospitalConfig] Error saving module ${moduleKey}:`, error);
    throw error;
  }
};

/**
 * Fetch recent audit trail history for configuration changes
 */
export const getConfigAuditLogs = async (limitCount = 25) => {
  try {
    const q = query(
      collection(db, 'config_audit_logs'),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );
    const snap = await (await import('firebase/firestore')).getDocs(q);
    return snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      timestamp: d.data().timestamp?.toDate?.() || new Date()
    }));
  } catch (error) {
    console.warn('⚠️ [HospitalConfig] Could not fetch audit logs:', error?.message);
    return [];
  }
};

export default {
  getHospitalConfig,
  subscribeHospitalConfig,
  requestEmailAuthCode,
  verifyEmailAuthCode,
  isEditSessionActive,
  getEditSessionRemainingSeconds,
  clearEditSession,
  updateHospitalConfigModule,
  getConfigAuditLogs
};
